import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle In-Dialog-Tour fuer den Dialog "Mitarbeiter zur Abteilung
 * zuweisen" (modules/organization/components/assign-employees-dialog →
 * AssignEmployeesDialogComponent). Geoeffnet ueber das "Mehr"-Menue einer
 * Abteilung/eines Projekts → "Mitarbeiter zuweisen" (Organisation → Liste,
 * Kanban, Organigramm oder Detailansicht). Wird via Hilfe-Button
 * (`<app-dialog-help-button>`) im Dialog-Header manuell gestartet.
 *
 * Pattern analog zu `PUBLISH_OPEN_SHIFTS_HELP_TOUR` / `ADD_SHIFT_HELP_TOUR`:
 * erklaerungs-getrieben (manuelles Weiterklicken, `nextCondition: 'manual'`),
 * nicht formular-gebunden. Cues pulsen die jeweils erwaehnten Elemente
 * synchron zum Voice-Over (Daten im Voice-Over-Manifest unter
 * `assign-employees-help`).
 *
 * Fachliche Wahrheiten (Backend: Application/Department/Commands/
 * AssignEmployeesToDepartment/AssignEmployeesToDepartmentCommandHandler.cs):
 * - SET-SEMANTIK: Die rechte Spalte ("Zugewiesene Mitarbeiter") IST der
 *   Soll-Zustand. Beim Speichern wird genau diese Liste zur Abteilungs-
 *   Mannschaft (Delta-Diff: Entfernte verlieren die Zuordnung, Neue werden
 *   zugewiesen).
 * - Skill-Match-Badge ("1/1" gruen = alle benoetigten Qualifikationen) ist
 *   eine reine Entscheidungs-/Sortier-Hilfe (EmployeeMatchingService), KEINE
 *   Sperre — jede Person ist zuweisbar.
 * - Eine Person kann zu MEHREREN Abteilungen gehoeren (1:n, Position-Entity,
 *   unique pro (Employee, Department)).
 * - Beim Speichern werden betroffene MA ueber Zu- bzw. Abgang benachrichtigt
 *   (NotificationType.DepartmentAssigned / DepartmentUnassigned, sofern Push
 *   aktiv). Permission: Department.CanAssignEmployeesToDepartments.
 * - Abteilung und Projekt werden identisch behandelt.
 *
 * Datenabhaengigkeit: Die Qualifikations-Leiste (.skills-info) rendert nur,
 * wenn die Abteilung Qualifikationen hinterlegt hat (*ngIf
 * requiredSkills.length>0). Der Step `skills` skippt daher ueber `skipIf`
 * (DOM-Existenz-Check) — bei einer Abteilung ohne Skills laeuft die Tour
 * ohne diesen Step (dann 4 statt 5 Schritte).
 */
export const ASSIGN_EMPLOYEES_HELP_TOUR: TourConfig = {
  id: TourFeature.AssignEmployeesHelp,
  version: 1,
  title: 'ASSIGN_EMPLOYEES_HELP_TOUR_TITLE',
  description: 'ASSIGN_EMPLOYEES_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // Help-Hub-Start: zur Organisation navigieren. Der Zuweisen-Dialog hat
  // keinen deterministischen Top-Level-Opener-Button (oeffnet ueber das
  // "Mehr"-Menue je Abteilung). Daher `route` only, ohne `opens`. Aus dem
  // In-Dialog-`?`-Button uebernimmt der bestehende Pfad (openDialogs.length>0).
  launchContext: {
    route: '/organization',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 80,
  // Bewusst 'feature-discovery' (NICHT 'contextual-help'): klassische Guided
  // Tour mit Spotlight + Voice-Over, nicht der HelpModeService mit (i)-Icons.
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'ASSIGN_EMPLOYEES_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'welcome',
          // Welcome erklaert den GANZEN Dialog — Spotlight am
          // .assign-dialog-container-Wrapper. Cues pulsen die beiden Spalten
          // beim Erwaehnen, um das Dual-List-Prinzip zu zeigen.
          anchor: AnchorId.AssignEmployeesDialog,
          title: 'TUTORIAL.ASSIGN_EMPLOYEES.WELCOME.TITLE',
          content: 'TUTORIAL.ASSIGN_EMPLOYEES.WELCOME.CONTENT',
          placement: 'auto',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          showPrevButton: false,
        },
        {
          id: 'skills',
          anchor: AnchorId.AssignEmployeesRequiredSkills,
          title: 'TUTORIAL.ASSIGN_EMPLOYEES.SKILLS.TITLE',
          content: 'TUTORIAL.ASSIGN_EMPLOYEES.SKILLS.CONTENT',
          placement: 'bottom',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          // .skills-info ist *ngIf="requiredSkills.length > 0" — fehlt bei
          // Abteilungen ohne hinterlegte Qualifikationen → Step uebersprungen.
          skipIf: () =>
            !document.querySelector(
              `[onboardingAnchor="${AnchorId.AssignEmployeesRequiredSkills}"]`
            ),
        },
        {
          id: 'available',
          anchor: AnchorId.AssignEmployeesAvailable,
          title: 'TUTORIAL.ASSIGN_EMPLOYEES.AVAILABLE.TITLE',
          content: 'TUTORIAL.ASSIGN_EMPLOYEES.AVAILABLE.CONTENT',
          placement: 'right',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
        },
        {
          id: 'assigned',
          anchor: AnchorId.AssignEmployeesAssigned,
          title: 'TUTORIAL.ASSIGN_EMPLOYEES.ASSIGNED.TITLE',
          content: 'TUTORIAL.ASSIGN_EMPLOYEES.ASSIGNED.CONTENT',
          placement: 'left',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
        },
        {
          id: 'save',
          anchor: AnchorId.AssignEmployeesSave,
          title: 'TUTORIAL.ASSIGN_EMPLOYEES.SAVE.TITLE',
          content: 'TUTORIAL.ASSIGN_EMPLOYEES.SAVE.CONTENT',
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          // Letzter Step: sauberer Abschluss, kein Zurueck-Button.
          showPrevButton: false,
        },
      ],
    },
  ],
};
